import './DesignColorOptions.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ColorPill from '@/components/Colorpill';
import Icon from '@/components/Icon/Icon';
import type { IDesignGroupColorOption } from '@/common/interfaces/design.interface';
import { STORE_CART_OPTION_CANCEL_ICON_SRC } from '@/modules/Cart/StoreCart/storeCart.constants';
import { getLatestDesignGroupOption } from '@/utilities/DesignUtils';

interface DesignColorOptionsProps {
  colors: IDesignGroupColorOption[];
  title: string;
  changeSelectedColorOption?: (newColorOption: IDesignGroupColorOption) => void;
  setSelectedColorOption?: (newColorOption: IDesignGroupColorOption) => void;
  onColorOptionClick?: (colorOption: IDesignGroupColorOption) => void;
  isInactive?: boolean;
  size?: 'extra-small' | 'small' | 'medium';
  showSelectedBadge?: boolean;
  /** When true, default color selection to the latest-added option. */
  selectLatestOption?: boolean;
}

const DesignColorOptions: React.FC<DesignColorOptionsProps> = ({
  colors,
  title,
  changeSelectedColorOption,
  setSelectedColorOption,
  onColorOptionClick,
  isInactive = false,
  size = 'medium',
  showSelectedBadge = false,
  selectLatestOption = false,
}) => {
  const getDefaultColor = useCallback(
    (colorOptions: IDesignGroupColorOption[]) =>
      selectLatestOption
        ? getLatestDesignGroupOption(colorOptions) ?? colorOptions[0] ?? null
        : colorOptions[0] || null,
    [selectLatestOption]
  );
  const [selectedColor, setSelectedColor] = useState<IDesignGroupColorOption | null>(
    getDefaultColor(colors)
  );

  const changeColorForProduct = useCallback(
    (color: IDesignGroupColorOption) => {
      if (setSelectedColorOption) setSelectedColorOption(color);
      if (changeSelectedColorOption) {
        changeSelectedColorOption(color);
      }
      setSelectedColor(color);
      if (onColorOptionClick) {
        onColorOptionClick(color);
      }
    },
    [changeSelectedColorOption, onColorOptionClick, setSelectedColorOption]
  );

  const getColorPillStyle = useCallback(
    (color: IDesignGroupColorOption) => {
      if (isInactive) {
        return {
          border: '1px solid #E9E9F0',
        };
      }

      return {
        border: selectedColor?.designId === color.designId ? '1px solid #4698EA' : 'none',
      };
    },
    [selectedColor, isInactive]
  );

  const renderColorOptions = useMemo(
    () =>
      colors.map((color) => (
        <div
          key={`${color.designId}`}
          className={`design-color-options__circle-wrapper design-color-options__circle-wrapper--${size}${
            selectedColor?.designId === color.designId
              ? ' design-color-options__circle-wrapper--selected'
              : ''
          }`}
          style={getColorPillStyle(color)}
          onClick={() => changeColorForProduct(color)}
        >
          <ColorPill backgroundColor={color?.colorHex} colorName={color.colorName} size={size} />
          {showSelectedBadge && selectedColor?.designId === color.designId && (
            <span className="design-color-options__selected-badge" aria-hidden="true">
              <Icon source={STORE_CART_OPTION_CANCEL_ICON_SRC} />
            </span>
          )}
          {/* {colors.length !== 1 && (
            <button
              type="button"
              className={`design-color-options__cancel-icon ${
                selectedColor?.designId !== color?.designId
                  ? 'design-color-options__cancel-icon--active'
                  : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleCancelColorOptionClick(color);
              }}
            >
              <Icon source="/src/assets/svg/cancel_profit.svg" />
            </button>
          )} */}
        </div>
      )),
    [
      changeColorForProduct,
      colors,
      getColorPillStyle,
      selectedColor?.designId,
      showSelectedBadge,
      size,
    ]
  );

  useEffect(() => {
    if (!selectedColor || !colors.some((c) => c.designId === selectedColor.designId)) {
      setSelectedColor(getDefaultColor(colors));
    }
    // else, keep the current selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors, getDefaultColor]);

  return (
    <div className="design-color-options">
      <div className="design-color-options__label">{title}</div>
      <div className="design-color-options__list">{renderColorOptions}</div>
    </div>
  );
};

export default React.memo(DesignColorOptions);
